import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pagination } from '../components/Pagination';
import { useStore } from '../context/StoreContext';
import { productBalanceApi, salesApi } from '../api/api';
import {
  parseProductBalanceIlist,
  syncProductBalancesToServer,
} from '../utils/productBalanceServer';
import { swalSuccess, swalError, swalWarning } from '../utils/swal';

/** Numeric server product id for API (local-only UUID products cannot be sold via API). */
function resolveServerProductId(product) {
  if (!product) return null;
  const raw =
    product.serverProductId != null
      ? product.serverProductId
      : product.id;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return Math.floor(n);
  return null;
}

function findProductById(products, cartProductId) {
  return products.find(
    (x) => String(x.id) === String(cartProductId)
  );
}

const SALES_REPORT_PER_PAGE = 5;

/** Same shape as other `ilist` parsers — `list_of_item` array or Laravel paginator. */
function parseSalesIlist(data) {
  const raw = data?.returnData?.list_of_item;
  if (!raw) {
    return {
      items: [],
      total: 0,
      lastPage: 1,
      from: 0,
      to: 0,
      mode: 'empty',
    };
  }
  if (Array.isArray(raw)) {
    const n = raw.length;
    return {
      items: raw,
      total: n,
      lastPage: 1,
      from: n ? 1 : 0,
      to: n,
      mode: 'array',
    };
  }
  if (raw.data && Array.isArray(raw.data)) {
    return {
      items: raw.data,
      total: raw.total ?? raw.data.length,
      lastPage: Math.max(1, raw.last_page ?? 1),
      from: raw.from ?? 0,
      to: raw.to ?? 0,
      mode: 'paginator',
    };
  }
  return {
    items: [],
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
    mode: 'empty',
  };
}

const POS_CATALOG_PER_PAGE = 100;

function pickNewerRawBalanceRow(a, b) {
  const aid = Number(a.id) || 0;
  const bid = Number(b.id) || 0;
  if (bid !== aid) return bid > aid ? b : a;
  const at = String(a.updated_at || a.created_at || '');
  const bt = String(b.updated_at || b.created_at || '');
  return bt >= at ? b : a;
}

/** One row per product when the API returns duplicate balance records. */
function dedupeRawBalanceRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const byPid = new Map();
  for (const row of rows) {
    const pid = row.product_id ?? row.product?.id ?? null;
    if (pid == null) continue;
    const key = String(pid);
    const prev = byPid.get(key);
    if (!prev) byPid.set(key, row);
    else byPid.set(key, pickNewerRawBalanceRow(prev, row));
  }
  return Array.from(byPid.values());
}

function mapRawBalanceRowToPosProduct(row) {
  const pid = row.product_id ?? row.product?.id ?? null;
  if (pid == null || Number.isNaN(Number(pid)) || Number(pid) <= 0) return null;
  const pr = row.product ?? {};
  const name = String(
    pr.name ?? row.name ?? row.product_name ?? 'Product'
  ).trim() || 'Product';
  const barcodeRaw = pr.barcode ?? row.barcode ?? row.product_barcode;
  const barcode =
    barcodeRaw != null && String(barcodeRaw).trim() !== ''
      ? String(barcodeRaw).trim()
      : '';
  const balance = Math.max(
    0,
    Math.floor(Number(row.balance ?? row.qty ?? 0))
  );
  const price = Math.max(
    0,
    Number(
      pr.selling_price ??
        row.selling_price ??
        row.price ??
        row.unit_price ??
        0
    )
  );
  const sellable = pr.can_be_sold !== 'no' && row.can_be_sold !== 'no';
  const n = Math.floor(Number(pid));
  return {
    id: String(pid),
    serverProductId: n,
    name,
    barcode,
    price,
    balance,
    sellable,
  };
}

function formatSaleDate(row) {
  const s =
    row.created_at ??
    row.created_at_date ??
    row.date ??
    row.applied_date ??
    '';
  if (!s) return '—';
  const str = String(s);
  if (str.includes('T')) return str.slice(0, 16).replace('T', ' ');
  return str.length > 10 ? str.slice(0, 10) : str;
}

function formatSaleAmount(row) {
  const n = Number(
    row.amount ?? row.total ?? row.total_amount ?? row.grand_total ?? 0
  );
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

function truncateCell(s, max) {
  const t = String(s ?? '').trim();
  if (!t) return '—';
  return t.length > max ? `${t.slice(0, max)}…` : t;
}

/** Resolve product display name from catalog by server / local id. */
function productNameFromCatalog(catalog, productId) {
  if (productId == null) return null;
  const key = String(productId);
  const p = catalog.find(
    (x) =>
      String(x.id) === key ||
      String(x.serverProductId ?? '') === key ||
      String(resolveServerProductId(x) ?? '') === key
  );
  return p?.name ? String(p.name).trim() : null;
}

/**
 * Human-readable product names for a sale row (API may nest `sales_items` or include names).
 */
function formatSaleProductNames(row, catalog) {
  const items = row.sales_items ?? row.salesItems ?? row.items ?? row.lines;
  if (Array.isArray(items) && items.length > 0) {
    const parts = items.map((it) => {
      const embedded =
        it.product_name ??
        it.product?.name ??
        it.name ??
        (typeof it.product === 'string' ? it.product : null);
      if (embedded != null && String(embedded).trim() !== '') {
        return String(embedded).trim();
      }
      const pid = it.product_id ?? it.product?.id ?? it.productId;
      if (pid != null) {
        const fromStore = productNameFromCatalog(catalog, pid);
        if (fromStore) return fromStore;
        return `Product #${pid}`;
      }
      return null;
    });
    const filtered = parts.filter(Boolean);
    if (filtered.length) return filtered.join(', ');
  }

  const top =
    row.product_name ??
    row.product?.name ??
    (row.product_id != null
      ? productNameFromCatalog(catalog, row.product_id)
      : null);
  if (top != null && String(top).trim() !== '') return String(top).trim();

  return '—';
}

/** Total quantity for a sale (sum of line `quantity` / `qty`, or top-level fields). */
function formatSaleQuantity(row) {
  const items = row.sales_items ?? row.salesItems ?? row.items ?? row.lines;
  if (Array.isArray(items) && items.length > 0) {
    const sum = items.reduce((acc, it) => {
      const q = Number(it.quantity ?? it.qty ?? 0);
      return acc + (Number.isFinite(q) ? q : 0);
    }, 0);
    return String(sum);
  }
  const direct = Number(
    row.quantity ?? row.total_quantity ?? row.qty ?? row.items_count
  );
  if (Number.isFinite(direct) && direct >= 0) return String(direct);
  return '—';
}

export function SalesPOS() {
  const [searchParams, setSearchParams] = useSearchParams();
  /** Avoid double-processing the same URL (e.g. React Strict Mode). Reset when `productId` is absent. */
  const lastProcessedPosQueryRef = useRef('');
  const { products, stockByProductId, checkout } = useStore();
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [discountInput, setDiscountInput] = useState('');
  const [taxRateInput, setTaxRateInput] = useState('0');
  const [payment, setPayment] = useState('cash');
  const [customerInput, setCustomerInput] = useState('');
  const [remarkInput, setRemarkInput] = useState('');
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const [tab, setTab] = useState('pos');
  const [reportRows, setReportRows] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportPage, setReportPage] = useState(1);
  const [reportMeta, setReportMeta] = useState({
    mode: 'empty',
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });

  const [balanceRows, setBalanceRows] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogSearchQ, setCatalogSearchQ] = useState('');
  const [catalogMeta, setCatalogMeta] = useState({
    mode: 'empty',
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });
  const [catalogHasLoaded, setCatalogHasLoaded] = useState(false);

  useEffect(() => {
    if (tab !== 'pos') return;
    const t = setTimeout(() => setCatalogSearchQ(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery, tab]);

  useEffect(() => {
    setCatalogPage(1);
  }, [catalogSearchQ]);

  const fetchPosBalanceCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError(null);
    try {
      const resp = await productBalanceApi.ilist({
        paginate: true,
        per_page: POS_CATALOG_PER_PAGE,
        page: catalogPage,
        q: catalogSearchQ,
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        setBalanceRows([]);
        setCatalogMeta({
          mode: 'empty',
          total: 0,
          lastPage: 1,
          from: 0,
          to: 0,
        });
        setCatalogError(data?.errorMessage || 'Failed to load products.');
        return;
      }
      const parsed = parseProductBalanceIlist(data);
      setBalanceRows(parsed.items);
      setCatalogMeta({
        mode: parsed.mode,
        total: parsed.total,
        lastPage: parsed.lastPage,
        from: parsed.from,
        to: parsed.to,
      });
    } catch (err) {
      setBalanceRows([]);
      setCatalogMeta({
        mode: 'empty',
        total: 0,
        lastPage: 1,
        from: 0,
        to: 0,
      });
      setCatalogError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          'Network error while loading products.'
      );
    } finally {
      setCatalogLoading(false);
      setCatalogHasLoaded(true);
    }
  }, [catalogPage, catalogSearchQ]);

  useEffect(() => {
    if (tab !== 'pos') return;
    fetchPosBalanceCatalog();
  }, [tab, fetchPosBalanceCatalog]);

  const posProducts = useMemo(() => {
    return dedupeRawBalanceRows(balanceRows)
      .map(mapRawBalanceRowToPosProduct)
      .filter(Boolean)
      .filter((p) => p.sellable);
  }, [balanceRows]);

  const posProductById = useMemo(() => {
    const m = new Map();
    for (const p of posProducts) m.set(String(p.id), p);
    return m;
  }, [posProducts]);

  const remainingForProduct = useCallback(
    (product) => {
      const inCart = cart.reduce(
        (sum, l) =>
          String(l.productId) === String(product.id) ? sum + l.qty : sum,
        0
      );
      return Math.max(0, product.balance - inCart);
    },
    [cart]
  );

  const stockCapForProductId = useCallback(
    (productId) => {
      const fromApi = posProductById.get(String(productId))?.balance;
      if (fromApi != null) return fromApi;
      return stockByProductId[productId] ?? 0;
    },
    [posProductById, stockByProductId]
  );

  const subtotal = useMemo(
    () => cart.reduce((sum, l) => sum + l.qty * l.unitPrice, 0),
    [cart]
  );

  const discountAmount = Math.min(
    Math.max(0, parseFloat(discountInput) || 0),
    subtotal
  );
  const taxable = subtotal - discountAmount;
  const taxRatePercent = Math.max(
    0,
    Math.min(100, parseFloat(taxRateInput) || 0)
  );
  const taxAmount =
    Math.round(taxable * (taxRatePercent / 100) * 100) / 100;
  const grandTotal = Math.round((taxable + taxAmount) * 100) / 100;

  const addToCart = useCallback(
    (product) => {
      setMessage(null);
      setCart((prev) => {
        const inCart = prev.reduce(
          (sum, l) =>
            String(l.productId) === String(product.id) ? sum + l.qty : sum,
          0
        );
        const available = Math.max(0, product.balance - inCart);
        const i = prev.findIndex(
          (l) => String(l.productId) === String(product.id)
        );
        if (i >= 0) {
          const next = [...prev];
          const line = next[i];
          if (line.qty + 1 > available) {
            setMessage({
              type: 'error',
              text: 'Not enough stock for that item.',
            });
            return prev;
          }
          next[i] = { ...line, qty: line.qty + 1 };
          return next;
        }
        if (available < 1) {
          setMessage({ type: 'error', text: 'Out of stock.' });
          return prev;
        }
        return [
          ...prev,
          {
            productId: product.id,
            serverProductId: product.serverProductId,
            name: product.name,
            barcode: product.barcode ?? '',
            qty: 1,
            unitPrice: product.price,
          },
        ];
      });
    },
    []
  );

  /** Open Sale from Products list: `/pos?productId=…` */
  useEffect(() => {
    if (tab !== 'pos') return;

    const pid = searchParams.get('productId');
    if (pid == null || String(pid).trim() === '') {
      lastProcessedPosQueryRef.current = '';
      return;
    }

    const queryKey = searchParams.toString();
    if (lastProcessedPosQueryRef.current === queryKey) return;

    if (catalogLoading || !catalogHasLoaded) return;

    const key = String(pid).trim();
    const match = posProducts.find(
      (p) =>
        String(p.id) === key || String(p.serverProductId) === key
    );

    const stripProductId = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('productId');
          return next;
        },
        { replace: true }
      );
    };

    if (!match) {
      lastProcessedPosQueryRef.current = queryKey;
      stripProductId();
      swalError(
        'Product not available',
        'This product is not in the current stock list. Check Stock Management or your search filter.'
      );
      return;
    }

    const available = remainingForProduct(match);
    lastProcessedPosQueryRef.current = queryKey;
    stripProductId();

    if (available < 1) {
      setMessage({ type: 'error', text: `Out of stock: ${match.name}.` });
      return;
    }

    addToCart(match);
    setMessage({
      type: 'ok',
      text: `Added ${match.name} from product list.`,
    });
  }, [
    searchParams,
    posProducts,
    catalogLoading,
    catalogHasLoaded,
    remainingForProduct,
    addToCart,
    setSearchParams,
    tab,
  ]);

  const fetchSalesReport = useCallback(async () => {
    setReportLoading(true);
    setReportError(null);
    try {
      const resp = await salesApi.ilist({
        paginate: true,
        per_page: SALES_REPORT_PER_PAGE,
        page: reportPage,
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        setReportRows([]);
        setReportMeta({
          mode: 'empty',
          total: 0,
          lastPage: 1,
          from: 0,
          to: 0,
        });
        setReportError(data?.errorMessage || 'Failed to load sales.');
        return;
      }
      const parsed = parseSalesIlist(data);
      setReportRows(parsed.items);
      setReportMeta({
        mode: parsed.mode,
        total: parsed.total,
        lastPage: parsed.lastPage,
        from: parsed.from,
        to: parsed.to,
      });
    } catch (err) {
      setReportRows([]);
      setReportError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          'Network error while loading sales.'
      );
    } finally {
      setReportLoading(false);
    }
  }, [reportPage]);

  useEffect(() => {
    if (tab !== 'report') return;
    fetchSalesReport();
  }, [tab, reportPage, fetchSalesReport]);

  const tryAddByNameEnter = useCallback(
    (raw) => {
      const q = raw.trim();
      if (!q) return;
      const lower = q.toLowerCase();
      const exact = posProducts.find(
        (p) => String(p.name ?? '').trim().toLowerCase() === lower
      );
      if (exact) {
        addToCart(exact);
        setSearchQuery('');
        setMessage({ type: 'ok', text: `Added ${exact.name}` });
        return;
      }
      if (posProducts.length === 1) {
        const only = posProducts[0];
        addToCart(only);
        setSearchQuery('');
        setMessage({ type: 'ok', text: `Added ${only.name}` });
        return;
      }
      setMessage({
        type: 'error',
        text:
          posProducts.length === 0
            ? 'No products match that name. Adjust your search.'
            : 'Type the full product name, narrow search to one row, or tap Add.',
      });
    },
    [posProducts, addToCart]
  );

  function onSearchKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryAddByNameEnter(searchQuery);
    }
  }

  function setLineQty(productId, qty) {
    const q = Math.max(0, parseInt(qty, 10) || 0);
    const product =
      posProductById.get(String(productId)) ??
      products.find((p) => String(p.id) === String(productId));
    const available = stockCapForProductId(productId);
    setMessage(null);
    if (q > available) {
      setMessage({
        type: 'error',
        text: `Only ${available} available for ${product?.name ?? 'item'}.`,
      });
      return;
    }
    setCart((prev) => {
      if (q === 0) return prev.filter((l) => l.productId !== productId);
      return prev.map((l) =>
        l.productId === productId ? { ...l, qty: q } : l
      );
    });
  }

  function removeLine(productId) {
    setCart((prev) => prev.filter((l) => l.productId !== productId));
  }

  async function handleCheckout() {
    setMessage(null);
    if (cart.length === 0) {
      swalError('Cannot complete sale', 'Cart is empty.');
      return;
    }

    const discountAmount = Math.min(
      Math.max(0, parseFloat(discountInput) || 0),
      subtotal
    );
    const taxRatePercent = Math.max(
      0,
      Math.min(100, parseFloat(taxRateInput) || 0)
    );
    const taxable = subtotal - discountAmount;
    const taxAmountCalc =
      Math.round(taxable * (taxRatePercent / 100) * 100) / 100;
    const totalAmount =
      Math.round((taxable + taxAmountCalc) * 100) / 100;

    const linesForStore = cart.map((l) => ({
      productId: l.productId,
      qty: l.qty,
      unitPrice: l.unitPrice,
    }));

    for (const line of linesForStore) {
      const available = stockCapForProductId(line.productId);
      if (line.qty > available) {
        swalError(
          'Could not complete sale',
          `Insufficient stock (need ${line.qty}, have ${available}).`
        );
        return;
      }
    }

    const salesItems = [];
    for (const line of cart) {
      const productId =
        line.serverProductId != null &&
        Number.isFinite(Number(line.serverProductId))
          ? Math.floor(Number(line.serverProductId))
          : resolveServerProductId(
              findProductById(products, line.productId)
            );
      if (productId == null || !Number.isFinite(productId)) {
        swalError(
          'Cannot complete sale',
          `Product "${line.name}" is not linked to the server catalog. Open Products to sync the catalog, then try again.`
        );
        return;
      }
      const totalPrice = Math.round(line.qty * line.unitPrice * 100) / 100;
      salesItems.push({
        product_id: productId,
        quantity: Number(line.qty),
        unit_price: Number(line.unitPrice),
        total_price: totalPrice,
      });
    }

    const customer =
      String(customerInput ?? '').trim() || 'Walk-in';
    const remarkParts = [
      String(remarkInput ?? '').trim(),
      `Payment: ${formatPaymentLabel(payment)}`,
      taxRatePercent > 0 ? `Tax ${taxRatePercent}%` : null,
    ].filter(Boolean);
    const remark = remarkParts.join(' · ') || 'POS sale';

    setCheckoutSubmitting(true);
    try {
      const resp = await salesApi.iformAction({
        form_method: 'save',
        amount: totalAmount,
        discount: discountAmount,
        remark,
        customer,
        status: 'completed',
        sales_items: salesItems,
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        const msg =
          data?.errorMessage ||
          data?.message ||
          'Server rejected the sale.';
        swalError('Could not save sale', msg);
        return;
      }

      const qtyByProductId = new Map();
      for (const line of cart) {
        qtyByProductId.set(
          line.productId,
          (qtyByProductId.get(line.productId) ?? 0) + line.qty
        );
      }
      const balanceSyncRows = [];
      for (const [productId, soldQty] of qtyByProductId) {
        const line = cart.find((l) => String(l.productId) === String(productId));
        const sid =
          line?.serverProductId != null &&
          Number.isFinite(Number(line.serverProductId))
            ? Math.floor(Number(line.serverProductId))
            : resolveServerProductId(findProductById(products, productId));
        if (sid == null) continue;
        const available = stockCapForProductId(productId);
        balanceSyncRows.push({
          serverProductId: sid,
          newBalance: Math.max(0, available - soldQty),
        });
      }
      const syncResult = await syncProductBalancesToServer(balanceSyncRows);

      const result = checkout(linesForStore, {
        paymentMethod: payment,
        discountAmount,
        taxRatePercent,
      });
      if (!result.ok) {
        swalError(
          'Sale saved on server',
          'Local stock could not be updated. Refresh or sync stock from Stock Management.'
        );
        return;
      }

      setCart([]);
      setDiscountInput('');
      setTaxRateInput('0');
      setSearchQuery('');
      const ref =
        data?.returnData?.id ??
        data?.returnData?.sale_id ??
        data?.returnData?.uuid;
      const refLabel =
        ref != null ? String(ref).slice(0, 12) : result.sale.id.slice(0, 8);
      const saleMsg = `Ref ${refLabel}… — TZS ${result.sale.total.toFixed(2)} (${formatPaymentLabel(payment)})`;
      if (!syncResult.ok && syncResult.failures.length > 0) {
        const ids = syncResult.failures.map((f) => `#${f.serverProductId}`).join(', ');
        swalWarning(
          'Sale complete',
          `${saleMsg} — Product balance could not be updated on the server for: ${ids}. Reconcile in Stock Management if needed.`
        );
      } else {
        swalSuccess('Sale complete', saleMsg);
      }
      fetchSalesReport();
      fetchPosBalanceCatalog();
    } catch (err) {
      const msg =
        err?.response?.data?.errorMessage ||
        err?.response?.data?.message ||
        err?.message ||
        'Network error while saving the sale.';
      swalError('Could not save sale', msg);
    } finally {
      setCheckoutSubmitting(false);
    }
  }

  return (
    <div className="page page-pos">
      <header className="page-header">
        <h1 className="page-title">Sale</h1>
        <p className="page-subtitle">
          {tab === 'pos'
            ? 'Search by product name to add items, set discount and tax, then take payment and complete the sale.'
            : 'Sales recorded on the server with pagination.'}
        </p>
      </header>

      <div className="card" style={{ marginBottom: '0.85rem' }}>
        <div
          className="card-toolbar"
          style={{ justifyContent: 'flex-start', gap: '0.5rem' }}
        >
          <button
            type="button"
            className={`btn btn-sm ${tab === 'pos' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab('pos')}
          >
            New sale
          </button>
          <button
            type="button"
            className={`btn btn-sm ${tab === 'report' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab('report')}
          >
            All sales
          </button>
        </div>
      </div>

      {tab === 'pos' && message && (
        <div
          className={`banner ${message.type === 'error' ? 'banner-error' : 'banner-ok'}`}
          role="status"
        >
          {message.text}
        </div>
      )}

      {tab === 'report' && (
        <section className="card">
          <div className="card-toolbar">
            <h2 className="card-title">Sales report</h2>
          </div>
          {reportError && (
            <p className="modal-error" role="alert" style={{ marginBottom: '0.6rem' }}>
              {reportError}
            </p>
          )}
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Products</th>
                  <th>Date</th>
                  <th>Quantity</th>
                  <th>Amount (TZS)</th>
                  <th>Status</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {reportLoading && reportRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty-cell">
                      Loading…
                    </td>
                  </tr>
                ) : !reportLoading && reportRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty-cell">
                      No sales yet.
                    </td>
                  </tr>
                ) : (
                  reportRows.map((row, idx) => (
                    <tr
                      key={
                        row.id != null
                          ? String(row.id)
                          : row.uuid != null
                            ? String(row.uuid)
                            : `sale-${reportPage}-${idx}`
                      }
                    >
                      <td>
                        {truncateCell(
                          formatSaleProductNames(row, products),
                          80
                        )}
                      </td>
                      <td className="mono">{formatSaleDate(row)}</td>
                      <td className="mono">{formatSaleQuantity(row)}</td>
                      <td className="mono">{formatSaleAmount(row)}</td>
                      <td>
                        <span className="badge badge-muted">
                          {truncateCell(row.status, 24)}
                        </span>
                      </td>
                      <td>{truncateCell(row.remark, 64)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {reportMeta.total > 0 && (
            <Pagination
              page={reportPage}
              totalPages={reportMeta.lastPage}
              onPageChange={setReportPage}
              total={reportMeta.total}
              from={reportMeta.from}
              to={reportMeta.to}
            />
          )}
        </section>
      )}

      {tab === 'pos' && (
      <div className="pos-grid">
        <section className="card pos-catalog">
          <div className="pos-catalog-toolbar">
            <h2 className="card-title pos-catalog-title">Products</h2>
            <div className="pos-search-row">
              <input
                className="input pos-search-input"
                type="search"
                placeholder="Search by name — Enter adds exact name or only match"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
                aria-label="Search products by name"
              />
            </div>
            {catalogError && (
              <p className="modal-error" role="alert" style={{ marginTop: '0.5rem' }}>
                {catalogError}
              </p>
            )}
          </div>
          <div className="table-wrap pos-product-table-wrap">
            <table className="table pos-product-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Quantity</th>
                  <th>Price (TZS)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {catalogLoading && posProducts.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="empty-cell">
                      Loading products…
                    </td>
                  </tr>
                ) : posProducts.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="empty-cell">
                      {catalogError
                        ? 'Could not load products from the server.'
                        : 'No sellable products in the stock list for this search.'}
                    </td>
                  </tr>
                ) : (
                  posProducts.map((p) => {
                    const remaining = remainingForProduct(p);
                    return (
                      <tr key={p.id}>
                        <td>{p.name}</td>
                        <td className="mono">{p.balance}</td>
                        <td className="mono">{p.price.toFixed(2)}</td>
                        <td className="table-actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => addToCart(p)}
                            disabled={remaining < 1 || catalogLoading}
                          >
                            Add
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {catalogMeta.mode === 'paginator' && catalogMeta.total > 0 && (
            <Pagination
              page={catalogPage}
              totalPages={catalogMeta.lastPage}
              onPageChange={setCatalogPage}
              total={catalogMeta.total}
              from={catalogMeta.from}
              to={catalogMeta.to}
            />
          )}
        </section>

        <section className="card pos-cart">
          <h2 className="card-title">This sale</h2>
          {cart.length > 0 ? (
            <div className="table-wrap pos-cart-table-wrap">
              <table className="table pos-cart-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Quantity</th>
                    <th>Price (TZS)</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {cart.map((line) => (
                    <tr key={line.productId}>
                      <td>{line.name}</td>
                      <td>
                        <input
                          className="input input--narrow"
                          type="number"
                          min="0"
                          max={stockCapForProductId(line.productId)}
                          value={line.qty}
                          onChange={(e) =>
                            setLineQty(line.productId, e.target.value)
                          }
                          aria-label={`Quantity for ${line.name}`}
                        />
                      </td>
                      <td className="mono">
                        {(line.qty * line.unitPrice).toFixed(2)}
                      </td>
                      <td className="table-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          onClick={() => removeLine(line.productId)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-hint cart-empty">No items yet.</p>
          )}

          <div className="cart-breakdown">
            <div className="cart-breakdown-row">
              <span>Subtotal (TZS)</span>
              <span className="mono">{subtotal.toFixed(2)}</span>
            </div>
            <label className="cart-breakdown-field">
              <span className="cart-breakdown-label">Discount (TZS)</span>
              <input
                className="input input--narrow cart-breakdown-input"
                type="number"
                min="0"
                step="0.01"
                value={discountInput}
                onChange={(e) => setDiscountInput(e.target.value)}
                placeholder="0"
              />
            </label>
            <label className="cart-breakdown-field">
              <span className="cart-breakdown-label">Tax rate (%)</span>
              <input
                className="input input--narrow cart-breakdown-input"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={taxRateInput}
                onChange={(e) => setTaxRateInput(e.target.value)}
              />
            </label>
            <div className="cart-breakdown-row cart-breakdown-row--muted">
              <span>Tax (TZS)</span>
              <span className="mono">{taxAmount.toFixed(2)}</span>
            </div>
            <div className="cart-total">
              <span>Total due (TZS)</span>
              <span className="cart-total-amount">{grandTotal.toFixed(2)}</span>
            </div>
          </div>

          <label className="field pos-cart-payment">
            <span className="field-label">Payment</span>
            <select
              className="input"
              value={payment}
              onChange={(e) => setPayment(e.target.value)}
              disabled={checkoutSubmitting}
            >
              <option value="cash">Cash</option>
              <option value="mobile_money">Mobile money</option>
              <option value="card">Card</option>
            </select>
          </label>

          <label className="field">
            <span className="field-label">Customer</span>
            <input
              className="input"
              type="text"
              value={customerInput}
              onChange={(e) => setCustomerInput(e.target.value)}
              placeholder="Walk-in"
              autoComplete="name"
              disabled={checkoutSubmitting}
              aria-label="Customer name"
            />
          </label>

          <label className="field">
            <span className="field-label">Remark</span>
            <input
              className="input"
              type="text"
              value={remarkInput}
              onChange={(e) => setRemarkInput(e.target.value)}
              placeholder="Optional note for this sale"
              disabled={checkoutSubmitting}
              aria-label="Sale remark"
            />
          </label>

          <button
            type="button"
            className="btn btn-primary btn-block btn-lg pos-cart-complete"
            onClick={handleCheckout}
            disabled={checkoutSubmitting}
          >
            {checkoutSubmitting ? 'Saving sale…' : 'Complete transaction'}
          </button>
        </section>
      </div>
      )}
    </div>
  );
}

function formatPaymentLabel(method) {
  switch (method) {
    case 'mobile_money':
      return 'Mobile money';
    case 'cash':
      return 'Cash';
    case 'card':
      return 'Card';
    default:
      return method;
  }
}
